import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { triggerConfettiFromElement, triggerGreenConfettiFromElement } from '@/utils/confetti';
import { useImposterAnimation } from '@/hooks/useImposterAnimation';
import { useNewApplicantAnimation } from '@/hooks/useNewApplicantAnimation';
import ImposterWarningPill from '@/components/ui/ImposterWarningPill';
import MatchLevelBadge from '@/components/ui/MatchLevelBadge';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { facelockStatusLabel } from '@/server/facelock/stateMapping';
import React, { RefCallback } from 'react';

interface Applicant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  applicationType: 'form' | 'mdl' | 'passport';
  issuingCountry?: string;
  documentNumber?: string;
  identityVerified: boolean;
  livenessVerified: boolean;
  approved: boolean;
  appliedAt: Date;
  lastUpdated: Date;
  matchLevel?: number;
  isPossibleImposter?: boolean;
  facelockStatus?: string;
  facelockSessionUrl?: string;
  facelockDisposition?: string;
}

interface StatusPillProps {
  label: string;
  completed: boolean;
  onClick?: () => void;
}

const StatusPill = React.forwardRef<HTMLButtonElement, StatusPillProps>(
  ({ label, completed, onClick }, ref) => {
    return (
      <button
        ref={ref}
        onClick={onClick}
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          completed
            ? 'text-green-800 bg-green-100 border border-green-200'
            : 'text-gray-600 bg-gray-100 border border-gray-200 hover:bg-gray-200'
        }`}
      >
        {completed && <CheckCircleSolid className="mr-1 w-3 h-3" />}
        {label}
      </button>
    );
  }
);

StatusPill.displayName = 'StatusPill';

interface ApplicantCardProps {
  applicant: Applicant;
  updating: string | null;
  livenessPillRef: RefCallback<HTMLButtonElement>;
  cardRef: RefCallback<HTMLDivElement>;
  isNewApplicant: boolean;
  onUpdateStatus: (applicantId: string, field: 'livenessVerified' | 'identityVerified' | 'approved', value: boolean) => void;
  onDelete: (applicant: Applicant) => void;
  formatDate: (date: Date) => string;
  formatRelativeDate: (date: Date) => string;
}

/**
 * Individual applicant card component
 * This component can use hooks properly since it's a full component
 */
const ApplicantCard: React.FC<ApplicantCardProps> = ({
  applicant,
  updating,
  livenessPillRef,
  cardRef,
  isNewApplicant,
  onUpdateStatus,
  onDelete,
  formatDate,
  formatRelativeDate,
}) => {
  // Use imposter animation hook at component level
  const imposterAnimationState = useImposterAnimation(applicant.id, applicant.isPossibleImposter || false);
  
  // Use new applicant animation hook
  const newApplicantAnimationState = useNewApplicantAnimation(applicant.id, isNewApplicant);
  
  // Determine card styling based on animation and imposter state
  // New applicant animation takes precedence over imposter animation
  const cardClasses = `rounded-2xl border p-4 shadow-sm transition-all duration-200 sm:p-6 ${
    newApplicantAnimationState.cardClasses || imposterAnimationState.cardClasses
  } ${
    imposterAnimationState.showWarning 
      ? 'bg-red-50 border-red-200' 
      : 'bg-white border-gray-200'
  }`;

  return (
    <div 
      ref={cardRef}
      className={cardClasses}
      role={imposterAnimationState.showWarning ? 'alert' : undefined}
      aria-live={imposterAnimationState.showWarning ? 'assertive' : 'off'}
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-start gap-3">
            <div className="min-w-0">
              <h3 className="font-serif text-xl font-semibold text-slate-950">
                {applicant.firstName} {applicant.lastName}
              </h3>
              <p className="text-sm text-gray-600">
                Submitted {formatRelativeDate(applicant.appliedAt)}
              </p>
              <p className="text-xs text-gray-500">
                {formatDate(applicant.appliedAt)}
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {(() => {
                // Normalize applicationType for display
                const normalizedApplicationType = applicant.applicationType ? String(applicant.applicationType).trim().toLowerCase() : '';
                
                const pillClass = normalizedApplicationType === 'mdl'
                  ? 'bg-blue-100 text-blue-800'
                  : normalizedApplicationType === 'passport'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-purple-100 text-purple-800';
                
                const pillText = normalizedApplicationType === 'mdl'
                  ? 'Mobile DL'
                  : normalizedApplicationType === 'passport'
                  ? 'Passport'
                  : 'Form';
                
                return (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pillClass}`}>
                    {pillText}
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 mb-3 md:grid-cols-2 md:gap-x-6 md:gap-y-2">
            <div className="flex gap-2 items-start">
              <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Email:</span>
              <span className="text-sm text-gray-900 break-words">{applicant.email || 'Not provided'}</span>
            </div>
            <div className="flex gap-2 items-start">
              <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Phone:</span>
              <span className="text-sm text-gray-900 break-words">
                {applicant.phone ? formatPhoneNumber(applicant.phone) : 'Not provided'}
              </span>
            </div>
            <div className="flex gap-2 items-start">
              <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Date of Birth:</span>
              <span className="text-sm text-gray-900">
                {new Date(applicant.dateOfBirth).toLocaleDateString()}
              </span>
            </div>
            <div className="flex gap-2 items-start">
              <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Address:</span>
              <span className="text-sm text-gray-900 break-words">{applicant.address}</span>
            </div>
            {(applicant.issuingCountry || applicant.documentNumber) && (
              <>
                <div className="flex gap-2 items-start">
                  <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Issuing Country:</span>
                  <span className="text-sm text-gray-900 break-words">
                    {applicant.issuingCountry || 'Not provided'}
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Document Number:</span>
                  <span className="font-mono text-sm text-gray-900 break-all">
                    {applicant.documentNumber || 'Not provided'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="w-full sm:ml-6 sm:w-56 sm:shrink-0">
          <div className="flex flex-col gap-2">
            <StatusPill
              label="Identity Document"
              completed={applicant.identityVerified}
              onClick={() => onUpdateStatus(applicant.id, 'identityVerified', !applicant.identityVerified)}
            />
            <StatusPill
              ref={livenessPillRef}
              label="Liveness Verified"
              completed={applicant.livenessVerified}
              onClick={() => {
                console.log('Liveness Verified clicked for applicant:', applicant.id, 'current value:', applicant.livenessVerified);
                onUpdateStatus(applicant.id, 'livenessVerified', !applicant.livenessVerified);
              }}
            />
            <StatusPill
              label="Approved"
              completed={applicant.approved}
            />
            {facelockStatusLabel(applicant.facelockStatus, applicant.facelockDisposition) && (
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                  applicant.facelockStatus === 'pending' || applicant.facelockStatus === 'in_progress'
                    ? 'text-blue-800 bg-blue-100 border-blue-200'
                    : applicant.facelockDisposition === 'accepted' || applicant.facelockDisposition === 'passed'
                    ? 'text-green-800 bg-green-100 border-green-200'
                    : applicant.facelockDisposition === 'rejected' || applicant.facelockStatus === 'failed'
                      ? 'text-red-800 bg-red-100 border-red-200'
                      : applicant.facelockStatus === 'expired' || applicant.facelockDisposition === 'needs_review'
                        ? 'text-amber-800 bg-amber-100 border-amber-200'
                        : 'text-blue-800 bg-blue-100 border-blue-200'
                }`}
              >
                FaceLock IDV: {facelockStatusLabel(applicant.facelockStatus, applicant.facelockDisposition)}
              </span>
            )}
            {applicant.facelockSessionUrl && (
              <a
                href={applicant.facelockSessionUrl}
                className="text-xs text-blue-700 underline"
                target="_blank"
                rel="noreferrer"
              >
                Open IDV link
              </a>
            )}
            
            {/* Match Level Badge - Only show if matchLevel exists, is >= 6 (not 0), and not an imposter */}
            {applicant.matchLevel && applicant.matchLevel !== 0 && applicant.matchLevel >= 6 && !applicant.isPossibleImposter && (
              <MatchLevelBadge matchLevel={applicant.matchLevel} />
            )}
            
            {/* Imposter Warning Pill */}
            <ImposterWarningPill show={imposterAnimationState.showWarning} />

            {/* Approve Button - Only active when both liveness and identity are verified */}
            <button
              onClick={() => onUpdateStatus(applicant.id, 'approved', true)}
              disabled={updating === applicant.id || !applicant.livenessVerified || !applicant.identityVerified || applicant.approved}
              className={`inline-flex min-h-11 items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2 ${
                applicant.livenessVerified && applicant.identityVerified && !applicant.approved
                  ? 'bg-primary-700 text-white hover:bg-primary-800'
                  : 'cursor-not-allowed bg-slate-200 text-slate-600'
              } disabled:cursor-not-allowed`}
            >
              {updating === applicant.id ? (
                'Updating...'
              ) : (
                <>
                  <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve Application
                </>
              )}
            </button>
            
            {/* Delete Button */}
            <button
              onClick={() => onDelete(applicant)}
              disabled={updating === applicant.id}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-600"
            >
              <TrashIcon className="mr-2 w-4 h-4" />
              Delete Application
            </button>
          </div>
          {updating === applicant.id && (
            <div className="mt-2 text-xs text-gray-500">Updating...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ApplicantsPage() {
  const location = useLocation();
  const facelockNotice = (location.state as { facelockNotice?: string } | null)?.facelockNotice;
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; applicant: Applicant | null }>({
    isOpen: false,
    applicant: null
  });
  
  // Refs to track status pill elements for each applicant
  const livenessPillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  
  // Refs to track card elements for each applicant
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Ref to track previous livenessVerified state for each applicant
  const previousLivenessState = useRef<Map<string, boolean>>(new Map());
  
  // Ref to track previous applicant IDs to detect new applicants
  const previousApplicantIds = useRef<Set<string>>(new Set());
  
  // Ref to track if this is the initial load
  const isInitialLoad = useRef<boolean>(true);
  
  // State to track which applicants are newly added
  const [newApplicantIds, setNewApplicantIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    console.log('Setting up Firebase listener for applicants collection...');

    // Set up real-time listener for applicants collection
    // Try with orderBy first, fallback to simple collection if it fails
    let q;
    try {
      q = query(collection(db, 'applicants'), orderBy('appliedAt', 'desc'));
    } catch (orderByError) {
      console.warn('orderBy failed, using simple collection query:', orderByError);
      q = collection(db, 'applicants');
    }
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log('Received Firebase snapshot with', querySnapshot.size, 'documents');

      const applicantsData: Applicant[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Processing applicant document:', doc.id, data);

        // Helper function to safely convert Firestore timestamp to Date
        const safeToDate = (timestamp: any): Date => {
          try {
            if (timestamp && typeof timestamp.toDate === 'function') {
              return timestamp.toDate();
            }
            if (timestamp instanceof Date) {
              return timestamp;
            }
            if (typeof timestamp === 'string') {
              return new Date(timestamp);
            }
            return new Date();
          } catch (error) {
            console.warn('Error converting timestamp:', error);
            return new Date();
          }
        };

        // Helper function to safely get string values
        const safeString = (value: any, defaultValue: string = ''): string => {
          if (typeof value === 'string') return value;
          if (value === null || value === undefined) return defaultValue;
          return String(value);
        };

        // Helper function to safely get boolean values
        const safeBoolean = (value: any, defaultValue: boolean = false): boolean => {
          if (typeof value === 'boolean') return value;
          if (value === null || value === undefined) return defaultValue;
          return Boolean(value);
        };

        try {
          // Normalize applicationType value - trim whitespace and convert to lowercase
          const normalizedApplicationType = data.applicationType ? String(data.applicationType).trim().toLowerCase() : '';
          const processedApplicationType = (['mdl', 'passport', 'form'].includes(normalizedApplicationType) 
            ? normalizedApplicationType 
            : 'form') as 'form' | 'mdl' | 'passport'; // Ensure valid applicationType value
          
          console.log(`Processing applicant ${doc.id}:`, {
            rawApplicationType: data.applicationType,
            normalizedApplicationType: normalizedApplicationType,
            processedApplicationType: processedApplicationType,
            applicationTypeType: typeof data.applicationType
          });
          
          applicantsData.push({
            id: doc.id,
            firstName: safeString(data.firstName, 'Unknown'),
            lastName: safeString(data.lastName, ''),
            email: safeString(data.email, ''),
            phone: safeString(data.phone, ''),
            dateOfBirth: safeString(data.dateOfBirth, ''),
            address: safeString(data.address, ''),
            applicationType: processedApplicationType,
            issuingCountry: safeString(data.issuingCountry),
            documentNumber: safeString(data.documentNumber),
            identityVerified: safeBoolean(data.identityVerified, false),
            livenessVerified: safeBoolean(data.livenessVerified, false),
            approved: safeBoolean(data.approved, false),
            appliedAt: safeToDate(data.appliedAt),
            lastUpdated: safeToDate(data.lastUpdated),
            matchLevel: typeof data.matchLevel === 'number' ? data.matchLevel : undefined,
            isPossibleImposter: safeBoolean(data.isPossibleImposter, false),
            facelockStatus: safeString(data.facelockStatus) || undefined,
            facelockSessionUrl: safeString(data.facelockSessionUrl) || undefined,
            facelockDisposition: safeString(data.facelockDisposition) || undefined,
          });
        } catch (error) {
          console.error('Error processing applicant document:', doc.id, error);
          // Skip malformed documents instead of crashing
        }
      });

      console.log('Processed applicants data:', applicantsData);
      
      // Sort by appliedAt if orderBy didn't work
      const sortedApplicants = applicantsData.sort((a, b) => {
        return b.appliedAt.getTime() - a.appliedAt.getTime();
      });
      
      // Detect new applicants (only after initial load)
      const currentApplicantIds = new Set(sortedApplicants.map(a => a.id));
      const newIds = new Set<string>();
      
      if (isInitialLoad.current) {
        // On initial load, just set the previous IDs without triggering animations
        previousApplicantIds.current = currentApplicantIds;
        isInitialLoad.current = false;
      } else {
        // After initial load, detect genuinely new applicants
        currentApplicantIds.forEach(id => {
          if (!previousApplicantIds.current.has(id)) {
            newIds.add(id);
          }
        });
        
        // Update new applicant IDs state (only if there are new ones)
        if (newIds.size > 0) {
          setNewApplicantIds(newIds);
        }
        
        // Update previous applicant IDs
        previousApplicantIds.current = currentApplicantIds;
      }
      
      setApplicants(sortedApplicants);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching applicants:', error);

      // For demo purposes, if Firebase permissions are not set up yet,
      // we'll show a helpful message instead of leaving the page loading
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes('Missing or insufficient permissions') ||
          errorMessage?.includes('permission-denied')) {
        console.warn('Firebase permissions not configured yet. Showing demo mode.');
        setApplicants([]); // Show empty state with helpful message
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Effect to detect new applicants and trigger confetti
  useEffect(() => {
    if (loading) return; // Skip on initial load
    
    newApplicantIds.forEach((applicantId) => {
      // Get the ref for this applicant's card
      const cardElement = cardRefs.current.get(applicantId);
      if (cardElement) {
        // Trigger green confetti from the card element
        triggerGreenConfettiFromElement(cardElement);
      }
    });
    
    // Clear new applicant IDs after a short delay to allow animation to complete
    if (newApplicantIds.size > 0) {
      const timeout = setTimeout(() => {
        setNewApplicantIds(new Set());
      }, 2000); // Clear after 2 seconds
      return () => clearTimeout(timeout);
    }
  }, [newApplicantIds, loading]);

  // Effect to detect livenessVerified changes and trigger confetti
  useEffect(() => {
    if (loading) return; // Skip on initial load
    
    applicants.forEach((applicant) => {
      const previousValue = previousLivenessState.current.get(applicant.id);
      const currentValue = applicant.livenessVerified;
      
      // Check if value changed from false to true
      if (previousValue === false && currentValue === true) {
        // Get the ref for this applicant's liveness pill
        const pillElement = livenessPillRefs.current.get(applicant.id);
        if (pillElement) {
          // Trigger confetti from the pill element
          triggerConfettiFromElement(pillElement);
        }
      }
      
      // Update previous state
      previousLivenessState.current.set(applicant.id, currentValue);
    });
  }, [applicants, loading]);

  const updateApplicantStatus = async (applicantId: string, field: 'livenessVerified' | 'identityVerified' | 'approved', value: boolean) => {
    console.log(`updateApplicantStatus called: applicantId=${applicantId}, field=${field}, value=${value}`);
    setUpdating(applicantId);
    try {
      const applicantRef = doc(db, 'applicants', applicantId);
      await updateDoc(applicantRef, {
        [field]: value,
        lastUpdated: new Date(),
      });
      console.log(`Updated ${field} status for applicant ${applicantId} to ${value}`);
    } catch (error) {
      console.error(`Error updating ${field} status:`, error);

      // For demo purposes, if Firebase permissions are not set up yet,
      // we'll show a warning but not crash the UI
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes('Missing or insufficient permissions') ||
          errorMessage?.includes('permission-denied')) {
        console.warn(`Firebase permissions not configured yet. Cannot update ${field} status.`);
        alert(`Demo mode: Cannot update status due to Firebase permissions. Please configure Firestore security rules first.`);
      }
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatRelativeDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
    }
  };

  const handleDeleteApplicant = async (applicantId: string) => {
    setUpdating(applicantId);
    try {
      await deleteDoc(doc(db, 'applicants', applicantId));
      console.log(`Deleted applicant ${applicantId}`);
    } catch (error) {
      console.error('Error deleting applicant:', error);
      
      // For demo purposes, if Firebase permissions are not set up yet,
      // we'll show a warning but not crash the UI
      if (error instanceof Error && (error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('permission-denied'))) {
        console.warn('Firebase permissions not configured yet. Cannot delete applicant.');
        alert('Demo mode: Cannot delete applicant due to Firebase permissions. Please configure Firestore security rules first.');
      } else {
        alert('Failed to delete applicant. Please try again.');
      }
    } finally {
      setUpdating(null);
      setDeleteConfirm({ isOpen: false, applicant: null });
    }
  };

  const openDeleteConfirm = (applicant: Applicant) => {
    setDeleteConfirm({ isOpen: true, applicant });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ isOpen: false, applicant: null });
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm" role="status">
          <div className="loading-spinner mx-auto mb-4 h-8 w-8 text-primary-700" />
          <p className="font-medium text-slate-800">Loading applications…</p>
          <p className="mt-1 text-sm text-slate-600">This usually takes a moment.</p>
        </div>
      </div>
    );
  }

  const noticeNeedsAttention = Boolean(facelockNotice && /could not|not be sent|unable/i.test(facelockNotice));

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link to="/" className="mb-3 inline-flex">
            <Button variant="ghost" size="sm">
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Application
            </Button>
          </Link>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-slate-950">Account Applicants</h1>
          <p className="mt-1 text-slate-600">Manage checking account applications</p>
        </div>
        <p className="text-sm font-medium text-slate-600">
          {applicants.length} applicant{applicants.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div>
        {facelockNotice && (
          <div
            className={`mb-6 rounded-2xl border px-4 py-4 text-sm leading-6 ${
              noticeNeedsAttention
                ? 'border-amber-300 bg-amber-50 text-amber-950'
                : 'border-green-300 bg-green-50 text-green-950'
            }`}
            role="status"
          >
            <p className="font-semibold">{noticeNeedsAttention ? 'Application saved' : 'Application received'}</p>
            <p className="mt-1">{facelockNotice}</p>
          </div>
        )}
        {applicants.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm">
            <div className="mb-4 text-slate-400">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="mb-2 font-serif text-2xl font-semibold text-slate-950">No applicants yet</h2>
            <p className="mx-auto mb-6 max-w-md text-slate-600">New applicants will appear here after they submit their account opening application.</p>
            <Link to="/">
              <Button>Go to Application Form</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {applicants.map((applicant) => (
              <ApplicantCard
                key={applicant.id}
                applicant={applicant}
                updating={updating}
                livenessPillRef={(el) => {
                  if (el) {
                    livenessPillRefs.current.set(applicant.id, el);
                  } else {
                    livenessPillRefs.current.delete(applicant.id);
                  }
                }}
                cardRef={(el) => {
                  if (el) {
                    cardRefs.current.set(applicant.id, el);
                  } else {
                    cardRefs.current.delete(applicant.id);
                  }
                }}
                isNewApplicant={newApplicantIds.has(applicant.id)}
                onUpdateStatus={updateApplicantStatus}
                onDelete={openDeleteConfirm}
                formatDate={formatDate}
                formatRelativeDate={formatRelativeDate}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm.isOpen && deleteConfirm.applicant && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-4 sm:items-center" role="presentation">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="delete-application-title">
            <div className="mb-4 flex items-center">
              <ExclamationTriangleIcon className="mr-3 h-6 w-6 text-red-700" />
              <h3 id="delete-application-title" className="font-serif text-xl font-semibold text-slate-950">Delete Application</h3>
            </div>
            
            <p className="mb-6 text-slate-700">
              Are you sure you want to delete the application for{' '}
              <strong>{deleteConfirm.applicant.firstName} {deleteConfirm.applicant.lastName}</strong>?
              This action cannot be undone.
            </p>
            
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteConfirm}
                disabled={updating === deleteConfirm.applicant.id}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDeleteApplicant(deleteConfirm.applicant!.id)}
                disabled={updating === deleteConfirm.applicant.id}
                className="text-white bg-red-600 border-red-600 hover:bg-red-700"
              >
                {updating === deleteConfirm.applicant.id ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
